import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

type PlatformUser = {
	id: string;
	name: string;
	email: string;
	emailVerified: boolean;
	image: string | null;
};

type PlatformAuthContextValue = {
	user: PlatformUser | null;
	loading: boolean;
	checkSession: () => Promise<void>;
	signOut: () => Promise<void>;
};

const PlatformAuthContext = createContext<PlatformAuthContextValue | null>(
	null,
);

export function PlatformAuthProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<PlatformUser | null>(null);
	const [loading, setLoading] = useState(true);

	const checkSession = useCallback(async () => {
		try {
			const res = await fetch("/api/platform/auth/get-session", {
				credentials: "include",
			});
			if (!res.ok) {
				setUser(null);
				return;
			}
			const data = await res.json();
			if (data && (data as { user?: PlatformUser }).user) {
				setUser((data as { user: PlatformUser }).user);
			} else {
				setUser(null);
			}
		} catch {
			setUser(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		checkSession();
	}, [checkSession]);

	const signOut = useCallback(async () => {
		try {
			await fetch("/api/platform/auth/sign-out", {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({}),
			});
		} catch {
			// proceed even on network error
		}
		setUser(null);
	}, []);

	return (
		<PlatformAuthContext.Provider
			value={{ user, loading, checkSession, signOut }}
		>
			{children}
		</PlatformAuthContext.Provider>
	);
}

export function usePlatformAuth() {
	const ctx = useContext(PlatformAuthContext);
	if (!ctx)
		throw new Error("usePlatformAuth must be used within PlatformAuthProvider");
	return ctx;
}
